import { get } from 'svelte/store';
import type { UINode, UIEdge } from '@rool/renderer';
import type { RoolChannel } from '@rool-dev/sdk';
import {
	nodes,
	edges,
	space,
	selectedNodeIds,
	canvasInstance,
} from '../stores';
import { CENTER_X, CENTER_Y } from './constants';
import {
	getCachedMediaUrl,
	resolveMediaUrl,
	getImageDimensions,
	isRoolMediaUrl,
	clearMediaCache,
	extractImageUrlsFromMarkdown,
	replaceMediaUrlsInMarkdown,
	isDomainCorsBlocked,
	proxyMediaUrl,
} from './media';
import { inferRenderType } from './infer-render-type';
import { parseCanvasType, applyFieldMap } from './canvas-type';
import { getCachedPdfPage, renderPdfPage } from './pdf';
import { grid, boundingRect } from '@rool/layout';
import type { LayoutNode } from '@rool/layout';
import { getCachedObjects } from './canvas-object-store';

/** Default node sizes by type */
const NODE_SIZES: Record<string, { width: number; height: number }> = {
	object: { width: 360, height: 160 },
	markdown: { width: 320, height: 220 },
	header: { width: 400, height: 80 },
	image: { width: 320, height: 240 },
	'sticky-note': { width: 200, height: 200 },
	topic: { width: 300, height: 120 },
	file: { width: 300, height: 300 },
};

/** Default node size */
const DEFAULT_SIZE = { width: 360, height: 160 };

/**
 * Node types that have dedicated renderers with specific data expectations.
 * Only these types should pass through from the backend — any other type
 * falls back to "object" to get the full key-value object dump.
 */
const SUPPORTED_RENDER_TYPES = new Set([
	'markdown',
	'image',
	'topic',
	'object',
	'header',
	'sticky-note',
	'pdf',
	'file',
]);

export function rebuildGraph() {
	const currentSpace = get(space);

	if (!currentSpace) {
		return;
	}

	// Get currently selected IDs to preserve selection
	const currentSelectedIds = get(selectedNodeIds);

	// Build lookup of current nodes for preserving locally-modified ones
	const currentNodes = get(nodes);
	const currentNodeMap = new Map<string, UINode>();
	for (const n of currentNodes) {
		currentNodeMap.set(n.id, n);
	}

	const nextNodes: UINode[] = [];
	const nextEdges: UIEdge[] = [];
	const noPositionIndices: number[] = [];
	const mediaUrlsToPreload: string[] = [];
	const imagesToResolveSize: Array<{ id: string; src: string }> = [];

	const objectCache = getCachedObjects();
	const ids = [...objectCache.keys()];
	ids.sort();

	for (let idx = 0; idx < ids.length; idx++) {
		const id = ids[idx];
		const obj: Record<string, unknown> = objectCache.get(id) ?? { id };
		const objType =
			typeof obj?.type === 'string' ? (obj.type as string) : 'object';

		// Get position from stored data or use default center (spread later).
		// Read _ prefixed fields first (hidden from AI), fall back to legacy unprefixed.
		let x: number;
		let y: number;
		let hasPosition: boolean;

		const storedX = obj._x ?? obj.x;
		const storedY = obj._y ?? obj.y;
		if (typeof storedX === 'number' && typeof storedY === 'number') {
			x = storedX;
			y = storedY;
			hasPosition = true;
		} else {
			x = CENTER_X;
			y = CENTER_Y;
			hasPosition = false;
		}

		// Type resolution precedence:
		// canvas_type → renderAs → SUPPORTED_RENDER_TYPES → inferRenderType → "object"
		let nodeType: string;
		const canvasType = parseCanvasType(obj.canvas_type);

		if (canvasType) {
			nodeType = canvasType.render_as;
		} else if (typeof obj.renderAs === 'string') {
			nodeType = obj.renderAs;
		} else if (SUPPORTED_RENDER_TYPES.has(objType)) {
			nodeType = objType;
		} else {
			const inferred = inferRenderType(obj);
			nodeType = inferred ?? 'object';
		}

		// Get size: use stored width/height if available, otherwise use defaults.
		// Read _ prefixed fields first, fall back to legacy unprefixed.
		let size: { width: number; height: number };
		const storedW = obj._width ?? obj.width;
		const storedH = obj._height ?? obj.height;
		const hasExplicitSize =
			typeof storedW === 'number' && typeof storedH === 'number';
		if (hasExplicitSize) {
			size = { width: storedW, height: storedH };
		} else {
			size = NODE_SIZES[nodeType] ?? DEFAULT_SIZE;
		}

		// Build node data - include both id and obj for compatibility with renderers
		// Deep clone the obj to avoid mutating the original
		const clonedObj = JSON.parse(JSON.stringify(obj));

		// Autofit flag recovery for headers.
		// The server subscription patch may not include the new boolean autofit flags
		// (autoSizeText, autoFitWidth, autoFitHeight, wordWrap). Two recovery paths:
		//
		// 1. Infer from legacy manuallyResized: if the server persisted manuallyResized
		//    but not autoSizeText, infer the full autofit settings from it.
		// 2. Preserve from current store: if the current store version has autofit flags
		//    that the SDK data doesn't, carry them forward across rebuilds.
		if (nodeType === 'header' && typeof clonedObj.autoSizeText !== 'boolean') {
			if (clonedObj.manuallyResized) {
				// Legacy flag present → infer autofit settings (user resized this header)
				clonedObj.autoSizeText = true;
				clonedObj.autoFitWidth = false;
				clonedObj.autoFitHeight = false;
				clonedObj.wordWrap = true;
			}
			delete clonedObj.manuallyResized;
		}

		// Preserve autofit flags from the current store when the SDK data doesn't
		// have them. This handles the case where a server subscription patch stripped
		// the flags after the grace period expired. The store always carries the
		// latest local state from the previous rebuild.
		if (currentNodeMap.has(id)) {
			const currentNode = currentNodeMap.get(id)!;
			const currentObj = (currentNode.data as Record<string, unknown>)?.obj as
				| Record<string, unknown>
				| undefined;
			if (currentObj) {
				for (const key of [
					'autoFitWidth',
					'autoFitHeight',
					'autoSizeText',
					'wordWrap',
				] as const) {
					if (
						typeof currentObj[key] === 'boolean' &&
						typeof clonedObj[key] !== 'boolean'
					) {
						clonedObj[key] = currentObj[key];
					}
				}
			}
		}

		// Apply canvas_type field mappings before type-specific normalization
		// so mapped fields feed into markdown/image/topic normalization below.
		if (canvasType) {
			applyFieldMap(clonedObj, canvasType.field_map);
		}

		// For markdown nodes: backend may store body text in `text` instead of `content`.
		// Normalize to `content` which is what MarkdownRenderer reads.
		if (nodeType === 'markdown') {
			if (!clonedObj.content && typeof clonedObj.text === 'string') {
				clonedObj.content = clonedObj.text;
			}
			if (typeof clonedObj.content === 'string') {
				// Collect Rool media URLs for preloading before replacing with cached/placeholder
				const markdownImageUrls = extractImageUrlsFromMarkdown(
					clonedObj.content,
				);
				mediaUrlsToPreload.push(...markdownImageUrls);
				clonedObj.content = replaceMediaUrlsInMarkdown(clonedObj.content);
			}
		}

		const nodeData: Record<string, unknown> = {
			id,
			obj: clonedObj,
		};

		// Image nodes: ImageRenderer expects `src` at top level of node.data.
		// Backend may store the image URL in `url`, `content`, or `src` field.
		// Use clonedObj (not obj) because canvas_type field_map may have mapped the URL.
		// For Rool SDK media URLs, we use the cached blob URL if available.
		if (nodeType === 'image') {
			const imgObj = clonedObj as Record<string, unknown>;
			const originalUrl = (imgObj.url ??
				imgObj.content ??
				imgObj.src ??
				imgObj.image ??
				'') as string;
			// Use cached blob URL for Rool media, or original URL for external images
			nodeData.src = getCachedMediaUrl(originalUrl);
			// Store original URL for cache lookup/refresh
			nodeData._originalSrc = originalUrl;
			if (imgObj.title) nodeData.title = imgObj.title;
			if (imgObj.caption) nodeData.caption = imgObj.caption;
			if (imgObj.alt) nodeData.alt = imgObj.alt;
			if (imgObj.objectFit) nodeData.objectFit = imgObj.objectFit;
			if (imgObj.labelPosition) nodeData.labelPosition = imgObj.labelPosition;
			if (imgObj.showLabels) nodeData.showLabels = imgObj.showLabels;
			// Collect for preloading and size resolution
			if (originalUrl) {
				mediaUrlsToPreload.push(originalUrl);
				if (!hasExplicitSize) {
					imagesToResolveSize.push({ id, src: originalUrl });
				}
			}
		}

		// PDF nodes: PdfRenderer expects pageImageUrl, currentPage, totalPages, title
		// The PDF media URL (sourceFile) needs to be resolved to a blob URL first,
		// then the page cache renders individual pages on demand.
		if (nodeType === 'pdf') {
			const pdfObj = clonedObj as Record<string, unknown>;
			const pdfMediaUrl = (pdfObj.sourceFile ?? '') as string;
			const currentPage = (pdfObj.currentPage as number) || 1;
			const totalPages = (pdfObj.totalPages as number) || 1;

			// Resolve the PDF media URL to a local blob URL (handles auth)
			const pdfBlobUrl = getCachedMediaUrl(pdfMediaUrl);
			if (pdfMediaUrl) {
				mediaUrlsToPreload.push(pdfMediaUrl);
			}

			// Try to get the cached rendered page; if not available, trigger async render
			let pageImageUrl = '';
			if (pdfBlobUrl) {
				const cached = getCachedPdfPage(pdfBlobUrl, currentPage);
				if (cached) {
					pageImageUrl = cached;
				} else {
					// Kick off async render — will trigger rebuildGraph when done
					renderPdfPage(pdfBlobUrl, currentPage).then((url) => {
						if (url) rebuildGraph();
					});
				}
			}

			nodeData.pageImageUrl = pageImageUrl;
			nodeData.currentPage = currentPage;
			nodeData.totalPages = totalPages;
			nodeData.src = pdfMediaUrl; // For AI visibility
			if (pdfObj.title) nodeData.title = pdfObj.title;
		}

		// File nodes: FileRenderer expects pageImageUrl (thumbnail) at top level of node.data.
		// The thumbnail URL (stored as `url`) needs to be resolved like images.
		if (nodeType === 'file') {
			const fileObj = clonedObj as Record<string, unknown>;
			const thumbnailUrl = (fileObj.url ?? '') as string;

			if (thumbnailUrl) {
				const cachedUrl = getCachedMediaUrl(thumbnailUrl);
				nodeData.pageImageUrl = cachedUrl || '';
				mediaUrlsToPreload.push(thumbnailUrl);
			}

			nodeData.src = (fileObj.sourceFile as string) ?? '';
			if (fileObj.title) nodeData.title = fileObj.title;
		}

		// Topic nodes: TopicRenderer expects title/description at top level of node.data,
		// but backend stores headline/bodyText (or text as fallback for body)
		if (nodeType === 'topic') {
			if (obj.headline) nodeData.title = obj.headline;
			if (obj.bodyText) {
				nodeData.description = obj.bodyText;
			} else if (typeof obj.text === 'string') {
				nodeData.description = obj.text;
			}
			if (obj.emoji) nodeData.emoji = obj.emoji;
		}

		// Position is top-left (matches SDK storage convention)
		nextNodes.push({
			id,
			type: nodeType,
			position: { x, y },
			z: currentSelectedIds.has(id) ? 100 : 0, // Selected nodes on top
			size,
			data: nodeData,
			selected: currentSelectedIds.has(id),
			...(typeof (obj._rotation ?? obj.rotation) === 'number' && {
				rotation: (obj._rotation ?? obj.rotation) as number,
			}),
		});

		if (!hasPosition) {
			noPositionIndices.push(nextNodes.length - 1);
		}

		// Build edges from reference fields.
		// In the new SDK model, references are data fields whose values are
		// object IDs (or arrays of object IDs). We detect these by checking
		// if string values match known object IDs in the cache.
		for (const [fieldName, fieldValue] of Object.entries(obj)) {
			if (fieldName === 'id' || fieldName.startsWith('_')) continue;
			const targets: string[] = Array.isArray(fieldValue)
				? fieldValue.filter((v): v is string => typeof v === 'string')
				: typeof fieldValue === 'string'
					? [fieldValue]
					: [];
			for (const targetId of targets) {
				if (targetId !== id && objectCache.has(targetId)) {
					nextEdges.push({
						id: `${id}:${fieldName}:${targetId}`,
						type: 'default',
						source: id,
						target: targetId,
						data: {
							label: fieldName,
						},
					});
				}
			}
		}
	}

	// Auto-layout unpositioned nodes using obstacle-aware grid
	if (noPositionIndices.length > 0) {
		// Build LayoutNode[] for nodes to place
		const toPlace: LayoutNode[] = noPositionIndices.map((idx) => {
			const n = nextNodes[idx];
			return {
				id: n.id,
				shape: {
					type: 'rect' as const,
					bounds: { x: 0, y: 0, width: n.size.width, height: n.size.height },
				},
			};
		});

		// Build obstacle list from positioned nodes (not in noPositionIndices)
		const unpositionedSet = new Set(noPositionIndices);
		const obstacles: LayoutNode[] = [];
		for (let i = 0; i < nextNodes.length; i++) {
			if (unpositionedSet.has(i)) continue;
			const n = nextNodes[i];
			obstacles.push({
				id: n.id,
				shape: {
					type: 'rect' as const,
					bounds: {
						x: n.position.x,
						y: n.position.y,
						width: n.size.width,
						height: n.size.height,
					},
				},
			});
		}

		// Compute grid origin:
		// 1. If nodes are selected → below selected nodes (AI context)
		// 2. No selection → viewport center (where the user is looking)
		// Within a row, successive nodes extend horizontally.
		let origin: { x: number; y: number };
		const selectedIds = currentSelectedIds;
		const selectedObstacles = obstacles.filter((o) => selectedIds.has(o.id));

		if (selectedObstacles.length > 0) {
			// Place below the selected nodes
			const anchorBounds = boundingRect(
				selectedObstacles.map(
					(o) =>
						(
							o.shape as {
								type: 'rect';
								bounds: { x: number; y: number; width: number; height: number };
							}
						).bounds,
				),
			);
			const rowY = anchorBounds.y + anchorBounds.height + 40;

			// Find the rightmost obstacle in this row (within vertical tolerance)
			// so successive single-node placements extend horizontally.
			const rowTolerance = 20;
			let rowRightEdge = anchorBounds.x;
			for (const obs of obstacles) {
				const b = (
					obs.shape as {
						type: 'rect';
						bounds: { x: number; y: number; width: number; height: number };
					}
				).bounds;
				if (Math.abs(b.y - rowY) < rowTolerance) {
					rowRightEdge = Math.max(rowRightEdge, b.x + b.width + 40);
				}
			}

			origin = {
				x: rowRightEdge === anchorBounds.x ? anchorBounds.x : rowRightEdge,
				y: rowY,
			};
		} else {
			// No selection — use the viewport center as origin
			const canvas = get(canvasInstance);
			if (canvas) {
				const camera = canvas.getCamera();
				origin = { x: camera.x, y: camera.y };
			} else {
				origin = { x: CENTER_X, y: CENTER_Y };
			}
		}

		const result = grid(
			{ nodes: toPlace, edges: [], obstacles },
			{
				avoidObstacles: true,
				origin,
				spacing: { x: 40, y: 40 },
				direction: 'row',
				count: toPlace.length, // single row — place all nodes horizontally
			},
		);

		// Apply result positions back to nextNodes and persist to SDK
		const positionsToPersist: { id: string; x: number; y: number }[] = [];
		for (let i = 0; i < noPositionIndices.length; i++) {
			const node = nextNodes[noPositionIndices[i]];
			const placed = result.nodes[i];
			node.position = { x: placed.x, y: placed.y };
			positionsToPersist.push({ id: node.id, x: placed.x, y: placed.y });
		}

		// Fire-and-forget: persist auto-layout positions so nodes don't jump
		// when other nodes are moved and the grid recalculates.
		if (currentSpace && positionsToPersist.length > 0) {
			Promise.all(
				positionsToPersist.map(({ id, x, y }) =>
					currentSpace.updateObject(id, { data: { _x: x, _y: y } }),
				),
			).catch((err) => {
				console.error('[graph] Failed to persist auto-layout positions:', err);
			});
		}
	}

	nodes.set(nextNodes);
	edges.set(nextEdges);

	// Preload media URLs: Rool URLs via auth, external URLs via CORS probe + proxy
	currentPreloadPromise = preloadMediaUrls(mediaUrlsToPreload);

	// For image nodes without stored dimensions, load images to determine aspect ratio
	resolveImageSizes(imagesToResolveSize);
}

/** Promise for the most recent media preload, so callers can await it */
let currentPreloadPromise: Promise<void> = Promise.resolve();

/**
 * Wait for any in-flight media preloading to complete.
 * Useful for keeping the splash screen visible until images are ready.
 */
export function waitForMediaPreload(): Promise<void> {
	return currentPreloadPromise;
}

/** Track URLs that have been preloaded to avoid duplicate rebuilds */
const preloadedUrls = new Set<string>();

/**
 * Clear media preload tracking (called when space changes).
 * This is separate from clearMediaCache because we need to track it here.
 */
export function clearPreloadedUrls(): void {
	preloadedUrls.clear();
}

/**
 * Preload media URLs collected during graph rebuild.
 *
 * - Rool SDK URLs: fetched via resolveMediaUrl (adds auth headers).
 * - External URLs: probed for CORS support (one HEAD per domain, cached).
 *   If CORS is blocked, fetched via proxyMediaUrl (server-side proxy).
 *   If CORS is ok, left as-is (the <img> tag loads them directly).
 *
 * When any URL is successfully resolved to a blob URL, triggers a rebuild
 * so getCachedMediaUrl returns the blob URL on the next render pass.
 */
async function preloadMediaUrls(urls: string[]): Promise<void> {
	const newUrls = urls.filter(
		(url) =>
			url &&
			!preloadedUrls.has(url) &&
			!url.startsWith('blob:') &&
			!url.startsWith('data:'),
	);

	if (newUrls.length === 0) return;

	// Mark as preloaded before fetching to prevent duplicate preloads
	for (const url of newUrls) {
		preloadedUrls.add(url);
	}

	// Split into Rool URLs (need auth) and external URLs (need CORS probe)
	const roolUrls = newUrls.filter((url) => isRoolMediaUrl(url));
	const externalUrls = newUrls.filter((url) => !isRoolMediaUrl(url));

	// Resolve Rool URLs via SDK auth
	const roolResults = await Promise.all(
		roolUrls.map((url) => resolveMediaUrl(url)),
	);

	// Probe external URLs for CORS and proxy those that are blocked
	const proxyResults = await Promise.all(
		externalUrls.map(async (url) => {
			const blocked = await isDomainCorsBlocked(url);
			if (!blocked) return null; // CORS ok — <img> loads it directly
			return proxyMediaUrl(url);
		}),
	);

	// If any URLs were resolved to blob URLs, rebuild to update src
	const anyResolved =
		roolResults.some((r) => r !== null) || proxyResults.some((r) => r !== null);
	if (anyResolved) {
		// Use setTimeout to avoid recursive rebuild during current rebuild
		setTimeout(() => rebuildGraph(), 0);
	}
}

/** Max/min bounds for auto-sized image nodes */
const IMAGE_MAX_WIDTH = 400;
const IMAGE_MAX_HEIGHT = 300;
const IMAGE_MIN_WIDTH = 180;
const IMAGE_MIN_HEIGHT = 120;

/** Track which image nodes are already being resolved to avoid duplicate work */
const pendingImageResolves = new Set<string>();

/**
 * Load images that don't have stored width/height, compute aspect-ratio-preserving
 * sizes, persist them to the SDK, and trigger a rebuild.
 * Accepts pre-collected image nodes from the rebuild loop, so this works for all
 * image nodes regardless of how their type was determined.
 * Uses fetchMedia for Rool SDK URLs to handle authentication.
 */
async function resolveImageSizes(
	images: Array<{ id: string; src: string }>,
): Promise<void> {
	const currentSpace = get(space) as any as RoolChannel | null;
	if (!currentSpace) return;

	for (const { id, src } of images) {
		if (pendingImageResolves.has(id)) continue;

		pendingImageResolves.add(id);

		// Use the media resolver which handles Rool SDK authentication
		resolveImageAndPersistSize(id, src, currentSpace);
	}
}

/**
 * Helper to resolve image dimensions and persist to SDK.
 */
async function resolveImageAndPersistSize(
	id: string,
	src: string,
	currentSpace: RoolChannel,
): Promise<void> {
	try {
		const dimensions = await getImageDimensions(src);
		pendingImageResolves.delete(id);

		if (!dimensions || dimensions.width === 0 || dimensions.height === 0) {
			return;
		}

		const aspect = dimensions.width / dimensions.height;
		let width = IMAGE_MAX_WIDTH;
		let height = width / aspect;

		if (height > IMAGE_MAX_HEIGHT) {
			height = IMAGE_MAX_HEIGHT;
			width = height * aspect;
		}

		width = Math.max(IMAGE_MIN_WIDTH, Math.round(width));
		height = Math.max(IMAGE_MIN_HEIGHT, Math.round(height));

		try {
			currentSpace.checkpoint('auto-size-image');
			await currentSpace.updateObject(id, {
				data: { _width: width, _height: height },
			});
			// objectUpdated from local_user is ignored, so rebuild manually
			rebuildGraph();
		} catch (err) {
			console.error('[graph] Failed to persist image size:', err);
		}
	} catch (err) {
		pendingImageResolves.delete(id);
		console.error('[graph] Failed to resolve image dimensions:', err);
	}
}
